import React from 'react';
import styled from '@emotion/styled';
import {
  calculateAge,
  DEFAULT_IMAGE_URL,
  formatExperienceDate,
} from '../../utils/utils';
import Img from '../../components/Img/Img';
import { capitalize } from 'lodash';
import { LockClosed16Solid } from '../../components/Icons/LockClosed16Solid';
import LicenseCard from '../../components/Card/LicenseCard';
import {
  SegmentedViewControllerLite,
  SegmentedViewDataLite,
  SegmentedViewLite,
} from '../../components/SegmentedView/SegmentedViewLite';
import ExperienceCard from '../../components/Card/ExperienceCard';
import EducationCard from '../../components/Card/EducationCard';
import type { ILocumData } from '../../models/directory/locum.model';
import { ModalProvider } from '../../context/ModalContext';
import ModalTriggerContainer from '../../components/Modals/ModalTriggerContainer';
import { Modal } from '../../components/Modals/Modal';
import GuestPromptModal from '../../components/Modals/ModalsActions/GuestPromptModal';

const segmentedControlConfData = [
  { title: 'Experience' },
  { title: 'Education' },
  { title: 'Licenses & certifications' },
];

type DummyLocumProfileProps = {
  locum: ILocumData;
  resume?: string | null;
};

const DummyLocumProfile: React.FC<DummyLocumProfileProps> = ({
  locum,
  resume,
}) => {
  if (!locum?.id) {
    return <Try>Select a Locum to view Profile.</Try>;
  }

  return (
    <>
      <BioSection>
        <BioImageArea>
          <BioImageCircle>
            <Img
              src={locum.profileImage ?? DEFAULT_IMAGE_URL}
              alt="profile image"
            />
          </BioImageCircle>
          <div>
            <BioName>
              {capitalize(locum.lastName ?? '')}{' '}
              {capitalize(locum.firstName ?? '')}
            </BioName>
            <BioOccupation>{locum.occupation ?? 'N/A'}</BioOccupation>
          </div>
        </BioImageArea>
        <Bio>{locum.bio ?? 'N/A'}</Bio>
      </BioSection>

      <BasicInformationSection>
        <BasicLabel>Basic information </BasicLabel>
        <AgeArea>
          <div>
            <Label>Age</Label>
            <DarkCell>
              {calculateAge(locum.birthday ?? '') || 'N/A'} years
            </DarkCell>
          </div>
          <div>
            <Label>Years of Experience</Label>
            <DarkCell>{locum.yearsOfExperience} years</DarkCell>
          </div>
          <div>
            <Label>Location</Label>
            <DarkCell>
              {locum.state ?? 'N/A'}, {locum.country ?? 'N/A'}
            </DarkCell>
          </div>

          <MobileViewAvailability>
            <Label>Availability</Label>
            <DarkCell>
              {locum.isAvailableForWork
                ? 'Available for work'
                : 'Not available'}
            </DarkCell>
          </MobileViewAvailability>
        </AgeArea>

        <Availability>
          <WebViewAvailability>
            <div>Availability</div>
            <DarkCell>
              {locum.isAvailableForWork
                ? 'Available for work'
                : 'Not available'}
            </DarkCell>
          </WebViewAvailability>

          <ModalProvider>
            <ModalTriggerContainer>
              <Button>Invite to apply</Button>
            </ModalTriggerContainer>

            <Modal>
              <GuestPromptModal locum />
            </Modal>
          </ModalProvider>

          <Button className="purple" disabled>
            {resume ? 'View CV' : 'No locum CV'}
            <LockClosed16Solid />
          </Button>
        </Availability>
      </BasicInformationSection>

      <ExperienceSection>
        <SegmentedViewLite>
          <SegmentedViewControllerLite
            segmentedViewControllerTitle={segmentedControlConfData}
            className="sm-header"
          />

          <SegmentedViewDataLite>
            {/* Experience */}
            <div>
              <SegmentContainer className="experiences">
                {locum.experiences?.length ? (
                  locum.experiences.map((data: any, index: any) => (
                    <ExperienceCard
                      key={data.id ?? index}
                      title={data.title}
                      companyName={data.companyName}
                      employmentType={data.employmentType}
                      location={data.location}
                      startDate={data.startDate}
                      endDate={data.endDate}
                    />
                  ))
                ) : (
                  <DarkCell>No experience added</DarkCell>
                )}
              </SegmentContainer>
            </div>

            {/* Education */}
            <div>
              <SegmentContainer>
                <EducationCard
                  school={locum.verification?.primaryDegreeName ?? 'N/A'}
                  license={
                    locum.verification?.primaryDegreeCertificate ?? 'N/A'
                  }
                  forApplicant
                />
              </SegmentContainer>
            </div>

            {/* Licenses & certifications */}
            <div>
              <SegmentContainer>
                {locum.verification || (locum.experiences?.length ?? 0) > 0 ? (
                  <Licenses>
                    {locum.verification && (
                      <LicenseCard
                        license={locum.verification.license ?? 'N/A'}
                        degree={locum.verification.primaryDegreeName ?? 'N/A'}
                        profession={locum.occupation ?? 'N/A'}
                        licensedAt={
                          formatExperienceDate(
                            locum.verification.yearOfCurrentLicense
                          ) ?? 'N/A'
                        }
                        forApplicant
                      />
                    )}

                    {locum.experiences
                      ?.filter((exp: any) => exp.otherQualification !== null)
                      .map((exp: any, index: any) => (
                        <LicenseCard
                          key={`qual-${exp.id ?? index}`}
                          degree={exp.otherQualification}
                          licensedAt={formatExperienceDate(
                            exp.yearOfQualification
                          )}
                          license={exp.qualificationCertificate ?? ''}
                          forApplicant
                        />
                      ))}
                  </Licenses>
                ) : (
                  'No licenses and certificates'
                )}
              </SegmentContainer>
            </div>
          </SegmentedViewDataLite>
        </SegmentedViewLite>
      </ExperienceSection>
    </>
  );
};

export default DummyLocumProfile;

/* ===================== styles (unchanged) ===================== */

const BioSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  grid-row: span 2;
`;

const BioImageArea = styled.div`
  display: flex;
  gap: 10px;
  font-size: 16px;
`;

const BioImageCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70px;
  width: 70px;
  overflow: hidden;
  border-radius: 50%;
  margin-bottom: 16px;
`;

const BioName = styled.div`
  display: flex;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackBlackMain};
`;

const BioOccupation = styled.div`
  display: flex;
  color: ${({ theme }) => theme.palette.mainBlue};
`;

const Bio = styled.div`
  display: flex;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack3};
  line-height: 24px;
  max-height: 320px;
  overflow: auto;
`;

const BasicInformationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.palette.greyGrey2};

  @media (max-width: 884px) {
    grid-row: 1;
    padding: 24px;
  }
`;

const BasicLabel = styled.div`
  color: ${({ theme }) => theme.palette.blackBlack3};
  font-weight: 500;
`;

const AgeArea = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Label = styled.div`
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Availability = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const MobileViewAvailability = styled.div`
  @media (min-width: 884px) {
    display: none;
  }
`;

const WebViewAvailability = styled.div`
  @media (max-width: 884px) {
    display: none;
  }
`;

const DarkCell = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey1};

  @media (max-width: 884px) {
    font-size: 14px;
  }
`;

const SegmentContainer = styled.div`
  display: flex;

  &.experiences {
    flex-direction: column;
    max-height: 320px;
    margin-left: -32px;
    margin-right: -32px;
    overflow-y: auto;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-height: fit-content;
    gap: 16px;

    &.experiences {
      flex-direction: column;
      margin: 0px;
    }
  }
`;

const Button = styled.button`
  display: flex;
  justify-content: space-around;
  color: white;
  font-weight: 500;
  font-size: 14px;
  height: fit-content;
  border-radius: 4px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.palette.mainBlue};
  cursor: pointer;
  width: 10rem;
  border: none;
  white-space: nowrap;

  &.purple {
    background-color: ${({ theme }) => theme.palette.mainPurple};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.greyGrey2};
  }
`;

const Try = styled.p`
  color: ${({ theme }) => theme.palette.greyGrey2};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
`;

const Licenses = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 2rem;
  padding-top: 1rem;
`;

const ExperienceSection = styled.div`
  display: flex;
`;
